import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EmployerPageShell from '../../components/employer/EmployerPageShell';
import api from '../../services/api';
import {
  acquireInterviewCamera,
  acquireInterviewMicrophone,
  connectInterviewMedia,
  prewarmInterviewAudioOutput,
  type BrowserTimingEvent,
  type InterviewAudioOutputWarmup,
} from './officialAvatarMedia';

type LifecycleState =
  | 'INITIALISING'
  | 'LISTENING'
  | 'RENDERING_RESPONSE'
  | 'SPEAKING'
  | 'RETURNING_TO_LISTENING'
  | 'CLOSING'
  | 'FINISHED'
  | 'FAILED';

type Profile = {
  id: string;
  displayName: string;
  defaultVoiceId: string;
  allowedVoiceIds: string[];
  portraitUrl: string;
  officialAvatarId: string;
};

type Voice = {
  id: string;
  displayName: string;
  provider: 'elevenlabs' | 'cartesia';
  modelId: string;
};

type DemoConfig = {
  enabled: boolean;
  ready: boolean;
  missingCredentialNames: string[];
  label: string;
  status: string;
  progress: number;
  access: string;
  fictionalDataOnly: boolean;
  recordingEnabled: boolean;
  egressEnabled: boolean;
  databaseWritesEnabled: false;
  productionReady: false;
  capacityPreparationBeforeBegin: true;
  candidateAttemptConsumedOnPreparation: false;
  preparationReadyTtlMs: number;
  rendererReady: boolean;
  streamerReady: boolean;
  listeningReady: boolean;
  speechBufferReady: boolean;
  officialWebRtcReady: boolean;
  profileRegistryValid: boolean;
  selectedAvatarProfileId: string;
  selectedVoiceId: string;
  transport: 'official-localrtc-webrtc';
  profiles: Profile[];
  voices: Voice[];
};

type SessionAccess = {
  id: string;
  roomName: string;
  liveKitUrl: string;
  accessToken: string;
  expiresAt: string;
  recordingStarted: boolean;
  databaseMutated: false;
  commerceMutated: false;
  avatarProfileId: string;
  voiceId: string;
  applicationId: string | null;
  jobId: string | null;
  questionCount: number;
  officialAvatarSessionId: string;
  officialWebRtcSignaling: 'backend-proxied';
  capacityPreparation: {
    used: boolean;
    rawInfrastructureReadyMs: number | null;
    readyBeforeBeginMs: number | null;
  };
};

type CapacityPreparation = {
  id: string;
  state: 'PROVISIONING' | 'READY' | 'FAILED';
  avatarProfileId: string;
  voiceId: string;
  requestedAt: string;
  readyAt: string | null;
  expiresAt: string | null;
  elapsedMs: number;
  rawInfrastructureReadyMs: number | null;
  attemptConsumed: false;
  interviewStarted: false;
  cancellationAllowed: true;
  retryAllowed: boolean;
  failureCode: string | null;
  message: string;
};

type TimingEvent = {
  type: string;
  relativeTimestampMs: number;
  metadata?: Record<string, unknown>;
};

type DemoStatus = {
  lifecycleState: LifecycleState;
  eventCount: number;
  participantState: {
    candidateJoined: boolean;
    candidateMicrophonePublished: boolean;
    candidateCameraPublished: boolean;
    agentJoined: boolean;
    liveKitInterviewerAudioPublished: boolean;
    officialWebRtcConnected: boolean;
    officialAudioConnected: boolean;
    officialVideoConnected: boolean;
    officialListeningInputActive: boolean;
  };
  completionEventSeen: boolean;
  failedEventSeen: boolean;
  rawTranscriptReturned: false;
  productionDataMutated: false;
  recordingStarted: boolean;
  egressStarted: boolean;
  timingEvents: TimingEvent[];
  events: Array<{
    type: string;
    relativeTimestampMs: number;
    metadata?: Record<string, unknown>;
  }>;
  latencySummaries: Array<Record<string, unknown>>;
  latencyIncomplete: Array<Record<string, unknown>>;
  officialStreamer: {
    ready: boolean;
    failed: boolean;
    browserReady: boolean;
    cleanupState: string;
    pcmPublishCount: number;
    duplicatePcmPublishCount: number;
    listenerPcmBytes: number;
    videoCodec: string;
    videoConfiguredTargetBitrateBps: number;
    videoEncoderTargetBitrateBps: number;
    videoOutboundBitrateBps: number;
    videoSourceHoldFrames: number;
    videoSourceFailures: number;
  };
};

type MediaConnection = Awaited<ReturnType<typeof connectInterviewMedia>>;
type Phase =
  | 'selecting'
  | 'preparing-capacity'
  | 'ready-to-begin'
  | 'preparation-failed'
  | 'connecting'
  | 'connected'
  | 'completed'
  | 'failed';
type CleanupRequest = {
  reason: 'terminal_status_acknowledged' | 'browser_exit' | 'user_ended' | 'abandoned_attempt' | 'failed_attempt';
  acknowledgedEventCount?: number;
  acknowledgedLifecycleState?: LifecycleState;
};

const lifecycleCopy: Record<LifecycleState, string> = {
  INITIALISING: 'Preparing interviewer',
  LISTENING: 'Listening',
  RENDERING_RESPONSE: 'Preparing a response',
  SPEAKING: 'Speaking',
  RETURNING_TO_LISTENING: 'Returning to listening',
  CLOSING: 'Completing interview',
  FINISHED: 'Interview complete',
  FAILED: 'Interview unavailable',
};

const lifecycleClasses: Record<LifecycleState, string> = {
  INITIALISING: 'border-slate-300 bg-slate-50 text-slate-700',
  LISTENING: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  RENDERING_RESPONSE: 'border-amber-300 bg-amber-50 text-amber-900',
  SPEAKING: 'border-blue-300 bg-blue-50 text-blue-900',
  RETURNING_TO_LISTENING: 'border-blue-300 bg-blue-50 text-blue-900',
  CLOSING: 'border-blue-300 bg-blue-50 text-blue-900',
  FINISHED: 'border-emerald-400 bg-emerald-50 text-emerald-950',
  FAILED: 'border-red-300 bg-red-50 text-red-900',
};

const displayError = (error: any, fallback: string) =>
  error?.response?.data?.code || (error instanceof Error ? error.message : fallback);

export const isRecoverableOwnedAvatarSignalingError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error || '');
  return /signal|websocket|abort handler|connection attempt|server unreachable/i.test(message);
};

export const isOwnedAvatarSessionNotFoundError = (error: any) =>
  error?.response?.data?.code === 'owned_avatar_demo_session_not_found';

const OwnedAvatarDemo: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const microphoneRef = useRef<MediaStream | null>(null);
  const cameraRef = useRef<MediaStream | null>(null);
  const candidatePreviewRef = useRef<HTMLVideoElement | null>(null);
  const candidateInsetRef = useRef<HTMLVideoElement | null>(null);
  const audioOutputWarmupRef = useRef<InterviewAudioOutputWarmup | null>(null);
  const mediaRef = useRef<MediaConnection | null>(null);
  const preparationRef = useRef<CapacityPreparation | null>(null);
  const mediaAttemptRef = useRef<{
    roomName: string;
    controller: AbortController;
  } | null>(null);
  const sessionRef = useRef<SessionAccess | null>(null);
  const connectionRoomRef = useRef<string | null>(null);
  const startInFlightRef = useRef(false);
  const recoveryUsedRef = useRef(false);
  const cleanedRoomsRef = useRef(new Set<string>());
  const terminalRoomsRef = useRef(new Set<string>());
  const portraitUrlsRef = useRef<Record<string, string>>({});
  const lifecycleStateRef = useRef<LifecycleState>('INITIALISING');
  const [config, setConfig] = useState<DemoConfig | null>(null);
  const [portraits, setPortraits] = useState<Record<string, string>>({});
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [session, setSession] = useState<SessionAccess | null>(null);
  const [preparation, setPreparation] = useState<CapacityPreparation | null>(null);
  const [preparationElapsedSeconds, setPreparationElapsedSeconds] = useState(0);
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [phase, setPhase] = useState<Phase>('selecting');
  const [lifecycleState, setLifecycleState] = useState<LifecycleState>('INITIALISING');
  const [microphoneStatus, setMicrophoneStatus] = useState('Not requested');
  const [cameraStatus, setCameraStatus] = useState('Not requested');
  const [preparingMessage, setPreparingMessage] = useState('Preparing interviewer…');
  const [error, setError] = useState<string | null>(null);
  const [completedRecordingId, setCompletedRecordingId] = useState<string | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [privateTranscript, setPrivateTranscript] = useState<Array<{ sequence: number; turn: string; text: string; acceptedAtMs: number }>>([]);
  const [recordingMessage, setRecordingMessage] = useState<string | null>(null);
  const [attemptProtectedAfterInfrastructureFailure, setAttemptProtectedAfterInfrastructureFailure] = useState(false);
  lifecycleStateRef.current = lifecycleState;

  const selectedProfile = useMemo(
    () => config?.profiles.find((profile) => profile.id === selectedProfileId) || null,
    [config?.profiles, selectedProfileId]
  );
  const selectedVoice = useMemo(
    () => config?.voices.find((voice) => voice.id === selectedProfile?.defaultVoiceId) || null,
    [config?.voices, selectedProfile]
  );
  const sessionRequestAllowed = Boolean(
    config?.enabled &&
    config.profileRegistryValid &&
    config.missingCredentialNames.length === 0 &&
    config.transport === 'official-localrtc-webrtc' &&
    config.recordingEnabled === config.egressEnabled &&
    config.databaseWritesEnabled === false
  );

  const stopMediaConnection = useCallback(async () => {
    const pending = mediaAttemptRef.current;
    mediaAttemptRef.current = null;
    pending?.controller.abort();
    const connection = mediaRef.current;
    mediaRef.current = null;
    if (connection) await connection.cleanup();
  }, []);

  const stopMicrophone = useCallback(() => {
    microphoneRef.current?.getTracks().forEach((track) => track.stop());
    microphoneRef.current = null;
    setMicrophoneStatus('Off');
  }, []);

  const stopCamera = useCallback(() => {
    cameraRef.current?.getTracks().forEach((track) => track.stop());
    cameraRef.current = null;
    if (candidatePreviewRef.current) candidatePreviewRef.current.srcObject = null;
    if (candidateInsetRef.current) candidateInsetRef.current.srcObject = null;
    setCameraStatus('Off');
  }, []);

  const stopAudioOutput = useCallback(async () => {
    const warmup = audioOutputWarmupRef.current;
    audioOutputWarmupRef.current = null;
    await warmup?.close();
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.srcObject = null;
  }, []);

  const releaseAttempt = useCallback(
    async (
      active: SessionAccess | null,
      {
        stopCapturedMicrophone = false,
        cleanupRequest = { reason: 'abandoned_attempt' },
      }: {
        stopCapturedMicrophone?: boolean;
        cleanupRequest?: CleanupRequest;
      } = {}
    ) => {
      let remoteCleanup: Promise<unknown> = Promise.resolve();
      if (active && !cleanedRoomsRef.current.has(active.roomName)) {
        cleanedRoomsRef.current.add(active.roomName);
        remoteCleanup = api
          .post(
            `/ai-interviews/owned-avatar-demo/sessions/${encodeURIComponent(active.roomName)}/cleanup`,
            cleanupRequest
          )
          .catch(() => undefined);
      }
      if (!active || sessionRef.current?.roomName === active.roomName) {
        connectionRoomRef.current = null;
        // Release the backend's single official-streamer slot concurrently with
        // local WebRTC/LiveKit disconnect. A LiveKit disconnect can take many
        // seconds; serialising these steps left the next selector start racing a
        // still-owned official session.
        await Promise.all([stopMediaConnection(), remoteCleanup]);
      } else {
        await remoteCleanup;
      }
      if (active && sessionRef.current?.roomName === active.roomName) {
        sessionRef.current = null;
        setSession(null);
      }
      if (stopCapturedMicrophone) {
        stopMicrophone();
        stopCamera();
        await stopAudioOutput();
      }
    },
    [stopAudioOutput, stopCamera, stopMediaConnection, stopMicrophone]
  );

  const cleanSession = useCallback(
    async (preserveCompletion = false, cleanupRequest: CleanupRequest = { reason: 'browser_exit' }) => {
      const active = sessionRef.current;
      await releaseAttempt(active, {
        stopCapturedMicrophone: true,
        cleanupRequest,
      });
      if (!preserveCompletion) setStatus(null);
    },
    [releaseAttempt]
  );

  const cancelActivePreparation = useCallback(async () => {
    const active = preparationRef.current;
    preparationRef.current = null;
    setPreparation(null);
    setPreparationElapsedSeconds(0);
    if (active) {
      await api
        .delete(`/ai-interviews/owned-avatar-demo/preparations/${encodeURIComponent(active.id)}`)
        .catch(() => undefined);
    }
    stopMicrophone();
    stopCamera();
    await stopAudioOutput();
  }, [stopAudioOutput, stopCamera, stopMicrophone]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/ai-interviews/owned-avatar-demo/config')
      .then(async (response) => {
        const next = response.data?.result as DemoConfig;
        if (cancelled) return;
        setConfig(next);
        setSelectedProfileId(next.selectedAvatarProfileId || next.profiles[0]?.id || '');
        const loaded = await Promise.all(
          next.profiles.map(async (profile) => {
            const portrait = await api.get(profile.portraitUrl, {
              responseType: 'blob',
            });
            const blob =
              portrait.data instanceof Blob ? portrait.data : new Blob([portrait.data], { type: 'image/png' });
            return [profile.id, URL.createObjectURL(blob)] as const;
          })
        );
        if (cancelled) {
          loaded.forEach(([, url]) => URL.revokeObjectURL(url));
          return;
        }
        const nextPortraits = Object.fromEntries(loaded);
        portraitUrlsRef.current = nextPortraits;
        setPortraits(nextPortraits);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setError(displayError(requestError, 'The interviewer experience could not be prepared.'));
        }
      });
    return () => {
      cancelled = true;
      Object.values(portraitUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      portraitUrlsRef.current = {};
    };
  }, []);

  useEffect(() => {
    const stream = cameraRef.current;
    for (const element of [candidatePreviewRef.current, candidateInsetRef.current]) {
      if (!element) continue;
      element.srcObject = stream;
      if (stream) void element.play().catch(() => undefined);
    }
  }, [cameraStatus, phase]);

  useEffect(() => {
    const closeOnExit = () => {
      void cleanSession();
      void cancelActivePreparation();
    };
    window.addEventListener('pagehide', closeOnExit);
    return () => {
      window.removeEventListener('pagehide', closeOnExit);
      void cleanSession();
      void cancelActivePreparation();
    };
  }, [cancelActivePreparation, cleanSession]);

  const reportBrowserEvent = useCallback(
    async (active: SessionAccess, type: BrowserTimingEvent, metadata?: Record<string, unknown>) => {
      const response = await api.post(
        `/ai-interviews/owned-avatar-demo/sessions/${encodeURIComponent(active.roomName)}/browser-events`,
        { type, metadata: metadata || {} }
      );
      return (response.data?.result || {}) as Record<string, unknown>;
    },
    []
  );

  useEffect(() => {
    if (!preparation || phase !== 'preparing-capacity') return;
    let cancelled = false;
    let pollInFlight = false;
    const startedAt = Date.now() - (preparationRef.current?.elapsedMs || 0);
    const updateElapsed = () => {
      if (!cancelled) {
        setPreparationElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1_000)));
      }
    };
    const poll = async () => {
      if (pollInFlight) return;
      pollInFlight = true;
      try {
        const response = await api.get(
          `/ai-interviews/owned-avatar-demo/preparations/${encodeURIComponent(preparation.id)}`
        );
        if (cancelled) return;
        const next = response.data?.result as CapacityPreparation;
        preparationRef.current = next;
        setPreparation(next);
        if (next.state === 'READY') {
          setPreparationElapsedSeconds(Math.max(0, Math.round((next.rawInfrastructureReadyMs || 0) / 1_000)));
          setPhase('ready-to-begin');
        } else if (next.state === 'FAILED') {
          setError('Your interviewer could not be prepared. Your interview attempt has not been used.');
          setPhase('preparation-failed');
        }
      } catch (pollError) {
        if (!cancelled) {
          setError(
            displayError(pollError, 'Your interviewer could not be prepared. Your interview attempt has not been used.')
          );
          setPhase('preparation-failed');
        }
      } finally {
        pollInFlight = false;
      }
    };
    updateElapsed();
    void poll();
    const elapsedTimer = window.setInterval(updateElapsed, 1_000);
    const pollTimer = window.setInterval(poll, 1_000);
    return () => {
      cancelled = true;
      window.clearInterval(elapsedTimer);
      window.clearInterval(pollTimer);
    };
  }, [phase, preparation?.id]);

  useEffect(() => {
    if (
      !session ||
      phase !== 'connecting' ||
      connectionRoomRef.current === session.roomName ||
      !videoRef.current ||
      !audioRef.current ||
      !microphoneRef.current ||
      (config?.recordingEnabled && !cameraRef.current)
    ) {
      return;
    }
    const microphoneTrack = microphoneRef.current.getAudioTracks()[0];
    if (!microphoneTrack) return;
    const cameraTrack = cameraRef.current?.getVideoTracks()[0];
    if (config?.recordingEnabled && !cameraTrack) return;
    connectionRoomRef.current = session.roomName;
    const attemptController = new AbortController();
    mediaAttemptRef.current = {
      roomName: session.roomName,
      controller: attemptController,
    };
    connectInterviewMedia({
      session,
      microphoneTrack,
      cameraTrack: config?.recordingEnabled ? cameraTrack : undefined,
      videoElement: videoRef.current,
      audioElement: audioRef.current,
      signal: attemptController.signal,
      isCurrent: () =>
        !attemptController.signal.aborted &&
        mediaAttemptRef.current?.controller === attemptController &&
        sessionRef.current?.roomName === session.roomName,
      isTerminalResponse: () => lifecycleStateRef.current === 'CLOSING' || lifecycleStateRef.current === 'FINISHED',
      callbacks: {
        iceServers: async (signal) => {
          const response = await api.get(
            `/ai-interviews/owned-avatar-demo/sessions/${encodeURIComponent(session.roomName)}/avatar/ice-servers`,
            { signal }
          );
          return {
            iceServers: response.data?.result?.iceServers || [],
            iceTransportPolicy: response.data?.result?.iceTransportPolicy || 'all',
          };
        },
        offer: async (offer, signal) => {
          const response = await api.post(
            `/ai-interviews/owned-avatar-demo/sessions/${encodeURIComponent(session.roomName)}/avatar/offer`,
            { sdp: offer.sdp, type: offer.type },
            { signal }
          );
          return response.data?.result;
        },
        report: (type, metadata) => reportBrowserEvent(session, type, metadata),
      },
      onCandidateMediaPublished: config?.recordingEnabled
        ? async () => {
            const response = await api.post(
              `/ai-interviews/owned-avatar-demo/sessions/${encodeURIComponent(session.roomName)}/recording/start`
            );
            if (response.data?.result?.state !== 'RECORDING') {
              throw new Error('owned_avatar_recording_not_ready');
            }
            setCameraStatus('On');
          }
        : undefined,
      audioOutputWarmup: audioOutputWarmupRef.current || undefined,
      onFatalMediaFailure: async (mediaError) => {
        if (
          mediaAttemptRef.current?.controller !== attemptController ||
          sessionRef.current?.roomName !== session.roomName
        )
          return;
        if (!recoveryUsedRef.current && microphoneRef.current?.getAudioTracks()[0]?.readyState === 'live') {
          recoveryUsedRef.current = true;
          setPreparingMessage('Restoring interviewer video…');
          setMicrophoneStatus('Reconnecting');
          setPhase('connecting');
          await releaseAttempt(session);
          try {
            const response = await api.post('/ai-interviews/owned-avatar-demo/sessions', {
              browserDevice: navigator.userAgent,
              avatarProfileId: session.avatarProfileId,
              voiceId: session.voiceId,
              ...(session.applicationId ? { applicationId: session.applicationId } : {}),
            });
            const replacement = response.data?.result as SessionAccess;
            sessionRef.current = replacement;
            connectionRoomRef.current = null;
            setSession(replacement);
            return;
          } catch (recoveryError) {
            mediaError = recoveryError instanceof Error ? recoveryError : mediaError;
          }
        }
        setError(
          `Connection failed. ${displayError(mediaError, 'The interviewer video stopped and could not be restored.')}`
        );
        setLifecycleState('FAILED');
        setPhase('failed');
        await releaseAttempt(session, { stopCapturedMicrophone: true });
      },
    })
      .then((connection) => {
        if (
          attemptController.signal.aborted ||
          mediaAttemptRef.current?.controller !== attemptController ||
          sessionRef.current?.roomName !== session.roomName
        ) {
          void connection.cleanup();
          return;
        }
        mediaRef.current = connection;
        setMicrophoneStatus('On');
        setLifecycleState('LISTENING');
        setPhase('connected');
      })
      .catch(async (connectionError) => {
        if (attemptController.signal.aborted && sessionRef.current?.roomName !== session.roomName) return;
        if (sessionRef.current?.roomName !== session.roomName) return;
        if (
          !recoveryUsedRef.current &&
          isRecoverableOwnedAvatarSignalingError(connectionError) &&
          microphoneRef.current?.getAudioTracks()[0]?.readyState === 'live'
        ) {
          recoveryUsedRef.current = true;
          setPreparingMessage('Reconnecting interviewer…');
          setMicrophoneStatus('Reconnecting');
          await releaseAttempt(session);
          try {
            const response = await api.post('/ai-interviews/owned-avatar-demo/sessions', {
              browserDevice: navigator.userAgent,
              avatarProfileId: session.avatarProfileId,
              voiceId: session.voiceId,
              ...(session.applicationId ? { applicationId: session.applicationId } : {}),
            });
            const replacement = response.data?.result as SessionAccess;
            sessionRef.current = replacement;
            connectionRoomRef.current = null;
            setSession(replacement);
            return;
          } catch (recoveryError) {
            connectionError = recoveryError;
          }
        }
        setError(
          `Connection failed. ${displayError(
            connectionError,
            'The interviewer stream could not be prepared. No fallback was used.'
          )}`
        );
        setLifecycleState('FAILED');
        setPhase('failed');
        await releaseAttempt(session, { stopCapturedMicrophone: true });
      });
  }, [config?.recordingEnabled, phase, releaseAttempt, reportBrowserEvent, session]);

  useEffect(() => {
    if (!session || !['connecting', 'connected'].includes(phase)) return;
    let cancelled = false;
    let pollInFlight = false;
    let terminalHandled = false;
    const poll = async () => {
      if (pollInFlight || terminalHandled || terminalRoomsRef.current.has(session.roomName)) return;
      pollInFlight = true;
      try {
        const response = await api.get(
          `/ai-interviews/owned-avatar-demo/sessions/${encodeURIComponent(session.roomName)}`
        );
        if (cancelled) return;
        const next = response.data?.result as DemoStatus;
        setStatus(next);
        setLifecycleState(next.lifecycleState);
        if (next.failedEventSeen || next.lifecycleState === 'FAILED') {
          terminalHandled = true;
          setError('The interview could not continue. No substitute voice or visual path was used.');
          await cleanSession(false, {
            reason: 'terminal_status_acknowledged',
            acknowledgedEventCount: next.eventCount,
            acknowledgedLifecycleState: next.lifecycleState,
          });
          if (!cancelled) setPhase('failed');
        } else if (next.completionEventSeen || next.lifecycleState === 'FINISHED') {
          terminalHandled = true;
          terminalRoomsRef.current.add(session.roomName);
          setLifecycleState('FINISHED');
          await cleanSession(true, {
            reason: 'terminal_status_acknowledged',
            acknowledgedEventCount: next.eventCount,
            acknowledgedLifecycleState: next.lifecycleState,
          });
          if (config?.recordingEnabled) {
            try {
              const recordingResponse = await api.get(
                `/ai-interviews/owned-avatar-demo/recordings/${encodeURIComponent(session.id)}`
              );
              if (recordingResponse.data?.result?.state !== 'COMPLETE') {
                throw new Error('owned_avatar_recording_completion_failed');
              }
              setCompletedRecordingId(session.id);
            } catch (recordingError) {
              setError(
                displayError(
                  recordingError,
                  'The interview completed, but its private recording could not be finalised.'
                )
              );
              setLifecycleState('FAILED');
              setPhase('failed');
              return;
            }
          }
          // Do not expose the selector until the single-session official
          // streamer has acknowledged teardown. Otherwise a fast click can
          // race the old session's LiveKit disconnect and receive a 409 from
          // the still-owned renderer slot.
          setPhase('completed');
        }
      } catch (pollError) {
        const expectedCompletedSessionAbsence =
          terminalRoomsRef.current.has(session.roomName) && isOwnedAvatarSessionNotFoundError(pollError);
        if (!cancelled && !terminalHandled && !expectedCompletedSessionAbsence) {
          terminalHandled = true;
          let protectedAttempt = false;
          if (config?.recordingEnabled && isOwnedAvatarSessionNotFoundError(pollError)) {
            try {
              const recovery = await api.post(
                `/ai-interviews/owned-avatar-demo/recordings/${encodeURIComponent(session.id)}/reconcile-interrupted`
              );
              protectedAttempt = recovery.data?.attemptProtected === true;
            } catch {
              protectedAttempt = false;
            }
          }
          setAttemptProtectedAfterInfrastructureFailure(protectedAttempt);
          setError(
            protectedAttempt
              ? 'Intellerview infrastructure interrupted this interview. Your attempt is protected and you can retry.'
              : displayError(pollError, 'The interview connection was lost.')
          );
          setLifecycleState('FAILED');
          setPhase('failed');
          await cleanSession(false, { reason: 'failed_attempt' });
        }
      } finally {
        pollInFlight = false;
      }
    };
    void poll();
    const timer = window.setInterval(poll, 750);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [cleanSession, config?.recordingEnabled, phase, session]);

  const beginPreparation = async () => {
    if (startInFlightRef.current || !sessionRequestAllowed || !selectedProfile || !selectedVoice) return;
    startInFlightRef.current = true;
    setError(null);
    setPreparationElapsedSeconds(0);
    setMicrophoneStatus('Not requested');
    setCameraStatus('Not requested');
    setPhase('preparing-capacity');
    try {
      const response = await api.post('/ai-interviews/owned-avatar-demo/preparations', {
        avatarProfileId: selectedProfile.id,
        voiceId: selectedVoice.id,
      });
      const next = response.data?.result as CapacityPreparation;
      preparationRef.current = next;
      setPreparation(next);
      if (next.state === 'READY') {
        setPreparationElapsedSeconds(Math.max(0, Math.round((next.rawInfrastructureReadyMs || 0) / 1_000)));
        setPhase('ready-to-begin');
      } else if (next.state === 'FAILED') {
        setError('Your interviewer could not be prepared. Your interview attempt has not been used.');
        setPhase('preparation-failed');
      }
    } catch (preparationError) {
      setError(
        displayError(
          preparationError,
          'Your interviewer could not be prepared. Your interview attempt has not been used.'
        )
      );
      setPhase('preparation-failed');
    } finally {
      startInFlightRef.current = false;
    }
  };

  const checkMicrophone = async () => {
    setError(null);
    const microphoneReady = microphoneRef.current?.getAudioTracks()[0]?.readyState === 'live';
    const cameraReady = cameraRef.current?.getVideoTracks()[0]?.readyState === 'live';
    if (microphoneReady && (!config?.recordingEnabled || cameraReady)) return;
    if (!microphoneReady) setMicrophoneStatus('Requesting permission');
    if (config?.recordingEnabled && !cameraReady) setCameraStatus('Requesting permission');
    try {
      if (!microphoneReady) {
        const microphone = await acquireInterviewMicrophone();
        microphoneRef.current = microphone.stream;
      }
      setMicrophoneStatus('Ready');
      if (config?.recordingEnabled && !cameraReady) {
        const camera = await acquireInterviewCamera();
        cameraRef.current = camera.stream;
        setCameraStatus('Ready');
      }
    } catch (microphoneError) {
      setMicrophoneStatus('Unavailable');
      if (config?.recordingEnabled) setCameraStatus('Unavailable');
      setError(displayError(microphoneError, 'Camera and microphone access are required before the interview can begin.'));
    }
  };

  const leavePreparation = async () => {
    setError(null);
    await cancelActivePreparation();
    setPhase('selecting');
  };

  const retryPreparation = async () => {
    await cancelActivePreparation();
    await beginPreparation();
  };

  const startInterview = async () => {
    const activePreparation = preparationRef.current;
    if (
      startInFlightRef.current ||
      !sessionRequestAllowed ||
      !selectedProfile ||
      !selectedVoice ||
      !audioRef.current ||
      !activePreparation ||
      activePreparation.state !== 'READY'
    )
      return;
    void audioOutputWarmupRef.current?.close();
    audioOutputWarmupRef.current = prewarmInterviewAudioOutput(audioRef.current);
    startInFlightRef.current = true;
    setError(null);
    setStatus(null);
    setMicrophoneStatus(
      microphoneRef.current?.getAudioTracks()[0]?.readyState === 'live' ? 'Connecting' : 'Requesting permission'
    );
    setPreparingMessage('Connecting interviewer…');
    setPhase('connecting');
    recoveryUsedRef.current = false;
    connectionRoomRef.current = null;
    try {
      const existingMicrophone = microphoneRef.current;
      const existingTrack = existingMicrophone?.getAudioTracks()[0];
      const existingCamera = cameraRef.current;
      const existingCameraTrack = existingCamera?.getVideoTracks()[0];
      const [microphoneResult, cameraResult, sessionResult] = await Promise.allSettled([
        existingMicrophone && existingTrack?.readyState === 'live'
          ? Promise.resolve({
              stream: existingMicrophone,
              track: existingTrack,
            })
          : acquireInterviewMicrophone(),
        config.recordingEnabled
          ? existingCamera && existingCameraTrack?.readyState === 'live'
            ? Promise.resolve({ stream: existingCamera, track: existingCameraTrack })
            : acquireInterviewCamera()
          : Promise.resolve(null),
        api.post('/ai-interviews/owned-avatar-demo/sessions', {
          browserDevice: navigator.userAgent,
          avatarProfileId: selectedProfile.id,
          voiceId: selectedVoice.id,
          preparationId: activePreparation.id,
          ...(applicationId.trim() ? { applicationId: applicationId.trim() } : {}),
        }),
      ]);
      if (
        microphoneResult.status === 'rejected' ||
        cameraResult.status === 'rejected' ||
        sessionResult.status === 'rejected'
      ) {
        if (microphoneResult.status === 'fulfilled') {
          microphoneResult.value.stream.getTracks().forEach((track) => track.stop());
        }
        if (cameraResult.status === 'fulfilled') {
          cameraResult.value?.stream.getTracks().forEach((track) => track.stop());
        }
        if (sessionResult.status === 'fulfilled') {
          const orphan = sessionResult.value.data?.result as SessionAccess;
          await releaseAttempt(orphan);
        }
        throw microphoneResult.status === 'rejected'
          ? microphoneResult.reason
          : cameraResult.status === 'rejected'
            ? cameraResult.reason
            : sessionResult.status === 'rejected'
              ? sessionResult.reason
              : new Error('owned_avatar_start_failed');
      }
      const microphone = microphoneResult.value;
      microphoneRef.current = microphone.stream;
      if (cameraResult.value) {
        cameraRef.current = cameraResult.value.stream;
        setCameraStatus('Connecting');
      }
      setMicrophoneStatus('Connecting');
      const next = sessionResult.value.data?.result as SessionAccess;
      preparationRef.current = null;
      setPreparation(null);
      sessionRef.current = next;
      setSession(next);
    } catch (startError) {
      setError(displayError(startError, 'The interviewer could not be started. No fallback was used.'));
      setLifecycleState('FAILED');
      setPhase('failed');
      await cancelActivePreparation();
      await cleanSession();
    } finally {
      startInFlightRef.current = false;
    }
  };

  const endInterview = async () => {
    setError(null);
    const active = sessionRef.current;
    if (active) terminalRoomsRef.current.add(active.roomName);
    await cleanSession(false, { reason: 'user_ended' });
    setLifecycleState('FINISHED');
    setPhase('completed');
  };

  const openPrivatePlayback = async () => {
    if (!completedRecordingId) return;
    setRecordingMessage('Preparing private playback…');
    try {
      const response = await api.post(
        `/ai-interviews/owned-avatar-demo/recordings/${encodeURIComponent(completedRecordingId)}/playback`
      );
      setPlaybackUrl(response.data?.result?.signedUrl || null);
      setRecordingMessage('Private playback is ready for five minutes.');
    } catch (playbackError) {
      setRecordingMessage(displayError(playbackError, 'Private playback is unavailable.'));
    }
  };

  const loadPrivateTranscript = async () => {
    if (!completedRecordingId) return;
    setRecordingMessage('Loading private transcript…');
    try {
      const response = await api.get(
        `/ai-interviews/owned-avatar-demo/recordings/${encodeURIComponent(completedRecordingId)}/transcript`
      );
      setPrivateTranscript(response.data?.result?.segments || []);
      setRecordingMessage('Private transcript loaded.');
    } catch (transcriptError) {
      setRecordingMessage(displayError(transcriptError, 'Private transcript is unavailable.'));
    }
  };

  const deletePrivateRecording = async () => {
    if (!completedRecordingId) return;
    setRecordingMessage('Deleting private interview media…');
    try {
      await api.delete(
        `/ai-interviews/owned-avatar-demo/recordings/${encodeURIComponent(completedRecordingId)}`
      );
      setPlaybackUrl(null);
      setPrivateTranscript([]);
      setRecordingMessage('The private recording and transcript were deleted.');
    } catch (deleteError) {
      setRecordingMessage(displayError(deleteError, 'Private media deletion failed.'));
    }
  };

  const startAnother = () => {
    connectionRoomRef.current = null;
    recoveryUsedRef.current = false;
    startInFlightRef.current = false;
    setError(null);
    setStatus(null);
    setLifecycleState('INITIALISING');
    setMicrophoneStatus('Not requested');
    setCameraStatus('Not requested');
    setPreparingMessage('Preparing interviewer…');
    preparationRef.current = null;
    setPreparation(null);
    setPreparationElapsedSeconds(0);
    setCompletedRecordingId(null);
    setPlaybackUrl(null);
    setPrivateTranscript([]);
    setRecordingMessage(null);
    setAttemptProtectedAfterInfrastructureFailure(false);
    setPhase('selecting');
  };

  const allPortraitsReady = Boolean(
    config?.profiles.length && config.profiles.every((profile) => portraits[profile.id])
  );
  const statusText = lifecycleCopy[lifecycleState];
  const isInterviewView = ['connecting', 'connected'].includes(phase);
  const latestCandidateEvent = [...(status?.events || [])]
    .reverse()
    .find((event) =>
      [
        'READINESS_LISTENING_STARTED',
        'candidate_speech_started',
        'STT_interim_received',
        'candidate_speech_ended',
        'candidate_utterance_ended',
        'STT_final_received',
        'transcript_accepted',
        'interview_decision_completed',
        'TTS_generation_started',
        'OWNED_AVATAR_SPEAKING_STARTED',
        'OWNED_AVATAR_SPEAKING_COMPLETED',
      ].includes(event.type)
    );
  const activeQuestionEvent = [...(status?.events || [])]
    .reverse()
    .find((event) => event.type === 'CORE_QUESTION_STARTED');
  const activeQuestionTimeLimitSeconds = Number(
    activeQuestionEvent?.metadata?.candidateTurnTimeLimitSeconds || 0
  );
  const candidatePipelineText = (() => {
    switch (latestCandidateEvent?.type) {
      case 'candidate_speech_started':
        return 'Hearing you…';
      case 'STT_interim_received':
        return 'Transcribing as you speak…';
      case 'candidate_speech_ended':
      case 'candidate_utterance_ended':
        return 'Finalising your response…';
      case 'STT_final_received':
      case 'transcript_accepted':
        return 'Response received';
      case 'interview_decision_completed':
      case 'TTS_generation_started':
        return 'Preparing interviewer response…';
      case 'OWNED_AVATAR_SPEAKING_STARTED':
        return 'Interviewer speaking';
      default:
        return 'Listening for your response';
    }
  })();
  const latestAudioProgress = [...(status?.events || [])]
    .reverse()
    .find((event) => event.type === 'candidate_audio_progress');
  const dualAudioActive =
    Number(latestAudioProgress?.metadata?.liveKitBytesSent || 0) > 0 &&
    Number(latestAudioProgress?.metadata?.officialBytesSent || 0) > 0;

  return (
    <EmployerPageShell>
      <div className="mx-auto w-full max-w-7xl space-y-6 pb-12" data-testid="official-owned-avatar-interview">
        <audio ref={audioRef} className="hidden" autoPlay preload="auto" aria-label="Official AVTR interviewer audio" />
        <header className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-700">AI Interview</p>
          <h1 className="text-3xl font-bold text-slate-950">{config?.label || 'Owned Avatar Product Demonstration'}</h1>
          <p className="max-w-3xl text-sm text-slate-600">
            Jason-only, isolated and non-production.{' '}
            {config?.recordingEnabled
              ? 'Private recording and transcript storage are enabled for this launch-gate proof.'
              : 'Recording and Egress are off. No candidate or business data is stored.'}
          </p>
          <p className="text-sm font-medium text-slate-700">
            {config?.status || 'In progress'} · {config?.progress ?? 82}% · not production ready
          </p>
        </header>

        {error && (
          <div role="alert" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
            {error}
          </div>
        )}

        {phase === 'selecting' && (
          <section className="space-y-5" aria-label="Choose an interviewer">
            <div>
              <h2 className="text-xl font-semibold text-slate-950">Choose your interviewer</h2>
              <p className="mt-1 text-sm text-slate-600">
                Select one of the four approved interviewer profiles before starting.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {(config?.profiles || []).map((profile) => {
                const voice = config?.voices.find((candidate) => candidate.id === profile.defaultVoiceId);
                const selected = profile.id === selectedProfileId;
                return (
                  <button
                    key={profile.id}
                    type="button"
                    onClick={() => setSelectedProfileId(profile.id)}
                    className={`overflow-hidden rounded-2xl border-2 bg-white text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                      selected ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-200 hover:border-slate-400'
                    }`}
                    aria-pressed={selected}
                    aria-label={`Select ${profile.displayName}`}
                  >
                    <div className="aspect-video bg-slate-100">
                      {portraits[profile.id] ? (
                        <img
                          src={portraits[profile.id]}
                          alt={`${profile.displayName} approved portrait`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-slate-500">
                          Loading interviewer…
                        </div>
                      )}
                    </div>
                    <div className="space-y-1 p-4">
                      <p className="font-semibold text-slate-950">{profile.displayName}</p>
                      <p className="text-sm text-slate-600">{voice?.displayName || 'Approved voice'}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            {!config?.enabled && config && (
              <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                The demonstration is disabled by default and is not available to public candidates.
              </p>
            )}
            <label className="block max-w-xl text-sm font-medium text-slate-800">
              Approved application ID
              <input
                type="text"
                value={applicationId}
                onChange={(event) => setApplicationId(event.target.value)}
                placeholder="Application UUID for the full approved job question plan"
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-mono text-sm text-slate-950 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
                aria-describedby="owned-avatar-application-help"
              />
              <span id="owned-avatar-application-help" className="mt-2 block font-normal text-slate-600">
                The authenticated backend resolves the application&apos;s stored Job questions. Leave blank only for the existing private one-question diagnostic.
              </span>
            </label>
            <button
              type="button"
              onClick={beginPreparation}
              disabled={!sessionRequestAllowed || !allPortraitsReady || !selectedProfile || !selectedVoice}
              className="rounded-xl bg-blue-700 px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              Continue to interview setup
            </button>
          </section>
        )}

        {['preparing-capacity', 'ready-to-begin', 'preparation-failed'].includes(phase) && (
          <section className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]" aria-label="Interview setup">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-blue-700">Before you begin</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-950">Get ready for your interview</h2>
              <ul className="mt-4 space-y-3 text-sm text-slate-700">
                <li>Choose a quiet place where you can speak naturally.</li>
                <li>Allow microphone access and check that your microphone is working.</li>
                {config?.recordingEnabled && <li>Allow camera access for the private candidate recording.</li>}
                <li>The interview starts only after you select Begin interview.</li>
                <li>Cancelling from this screen does not use your interview attempt.</li>
              </ul>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={checkMicrophone}
                  disabled={microphoneStatus === 'Requesting permission'}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                >
                  {config?.recordingEnabled ? 'Check camera and microphone' : 'Check microphone'}
                </button>
                <span className="text-sm text-slate-700">Microphone: {microphoneStatus}</span>
                {config?.recordingEnabled && (
                  <span className="text-sm text-slate-700">Camera: {cameraStatus}</span>
                )}
              </div>
              {config?.recordingEnabled && (
                <video
                  ref={candidatePreviewRef}
                  className="mt-5 aspect-video w-full max-w-sm rounded-xl bg-slate-950 object-cover"
                  autoPlay
                  playsInline
                  muted
                  aria-label="Candidate camera preview"
                />
              )}
            </div>

            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 shadow-sm" aria-live="polite">
              <div className="flex items-center gap-3">
                {phase === 'preparing-capacity' && (
                  <div className="h-7 w-7 animate-spin rounded-full border-2 border-blue-300 border-t-blue-700" />
                )}
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">
                    {phase === 'ready-to-begin'
                      ? 'Your interviewer is ready'
                      : phase === 'preparation-failed'
                        ? 'Preparation could not finish'
                        : 'Preparing your interviewer'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-700">
                    {phase === 'ready-to-begin'
                      ? 'You can begin when you are ready.'
                      : phase === 'preparation-failed'
                        ? 'You can retry safely. Your interview attempt has not been used.'
                        : `The interview has not started. Preparation time: ${preparationElapsedSeconds} seconds.`}
                  </p>
                </div>
              </div>
              <p className="mt-4 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-950">
                Your interview attempt has not been used.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                {phase === 'ready-to-begin' && (
                  <button
                    type="button"
                    onClick={startInterview}
                    className="rounded-xl bg-blue-700 px-5 py-2.5 font-semibold text-white hover:bg-blue-800"
                  >
                    Begin interview
                  </button>
                )}
                {phase === 'preparation-failed' && (
                  <button
                    type="button"
                    onClick={retryPreparation}
                    className="rounded-xl bg-blue-700 px-5 py-2.5 font-semibold text-white hover:bg-blue-800"
                  >
                    Retry preparation
                  </button>
                )}
                <button
                  type="button"
                  onClick={leavePreparation}
                  className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Cancel preparation
                </button>
              </div>
            </div>
          </section>
        )}

        {isInterviewView && (
          <section className="space-y-4" aria-label="Connected interview">
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-950 shadow-xl">
              <video
                ref={videoRef}
                className={`h-full w-full object-contain transition-opacity duration-500 ${
                  phase === 'connected' ? 'opacity-100' : 'opacity-0'
                }`}
                autoPlay
                playsInline
                muted
                aria-label="Official AVTR interviewer video"
              />
              {phase === 'connecting' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-slate-950 text-white">
                  {portraits[selectedProfileId] && (
                    <img
                      src={portraits[selectedProfileId]}
                      alt=""
                      className="h-20 w-20 rounded-full object-cover opacity-70"
                    />
                  )}
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-500 border-t-white" />
                  <p className="text-lg font-medium">{preparingMessage}</p>
                  <p className="text-sm text-slate-300">This can take a moment.</p>
                </div>
              )}
              {config?.recordingEnabled && (
                <video
                  ref={candidateInsetRef}
                  className="absolute bottom-4 right-4 aspect-video w-44 rounded-lg border border-white/50 bg-slate-900 object-cover shadow-lg"
                  autoPlay
                  playsInline
                  muted
                  aria-label="Candidate recording preview"
                />
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className={`rounded-full border px-3 py-1 font-medium ${lifecycleClasses[lifecycleState]}`}>
                  {statusText}
                </span>
                <span className="text-slate-700">Microphone: {microphoneStatus}</span>
                {phase === 'connected' && (
                  <span className="font-medium text-slate-800" aria-live="polite">
                    Speech: {candidatePipelineText}
                  </span>
                )}
                {phase === 'connected' && activeQuestionTimeLimitSeconds > 0 && (
                  <span className="text-slate-600">
                    Maximum answer time: {activeQuestionTimeLimitSeconds / 60} minutes
                  </span>
                )}
                {phase === 'connected' && (
                  <span className="text-slate-600">
                    Audio routes: {dualAudioActive ? 'LiveKit + interviewer listening active' : 'Checking…'}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={endInterview}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                End interview
              </button>
            </div>
          </section>
        )}

        {phase === 'completed' && (
          <section className="rounded-2xl border border-emerald-300 bg-emerald-50 p-8 text-center">
            <h2 className="text-2xl font-semibold text-emerald-950">Interview complete</h2>
            <p className="mt-2 text-sm text-emerald-900">
              {completedRecordingId
                ? 'The interview and private recording completed successfully.'
                : 'The interview connections have been closed. No recording was made.'}
            </p>
            {completedRecordingId && (
              <div className="mx-auto mt-5 max-w-3xl space-y-4 rounded-xl border border-emerald-200 bg-white p-5 text-left">
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={openPrivatePlayback} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white">
                    Play private recording
                  </button>
                  <button type="button" onClick={loadPrivateTranscript} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800">
                    View transcript
                  </button>
                  <button type="button" onClick={deletePrivateRecording} className="rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-800">
                    Delete recording and transcript
                  </button>
                </div>
                {recordingMessage && <p className="text-sm text-slate-700">{recordingMessage}</p>}
                {playbackUrl && <video src={playbackUrl} controls className="aspect-video w-full rounded-lg bg-black" />}
                {privateTranscript.length > 0 && (
                  <ol className="space-y-2 text-sm text-slate-800">
                    {privateTranscript.map((segment) => (
                      <li key={segment.sequence}>
                        <span className="font-semibold">{segment.turn} · {Math.round(segment.acceptedAtMs / 1000)}s:</span>{' '}
                        {segment.text}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={startAnother}
              className="mt-5 rounded-xl bg-emerald-800 px-5 py-2.5 font-semibold text-white hover:bg-emerald-900"
            >
              Choose another interviewer
            </button>
          </section>
        )}

        {phase === 'failed' && (
          <section className="rounded-2xl border border-slate-300 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-950">Connection failed</h2>
            <p className="text-sm text-slate-700">
              {attemptProtectedAfterInfrastructureFailure
                ? 'Intellerview stopped the interrupted session safely. This infrastructure failure did not use your interview attempt, so you may retry.'
                : 'The isolated session has been closed. No fallback provider or renderer was used.'}
            </p>
            <button
              type="button"
              onClick={startAnother}
              className="mt-4 rounded-xl border border-slate-300 px-5 py-2.5 font-semibold text-slate-800 hover:bg-slate-50"
            >
              Return to interviewer selection
            </button>
          </section>
        )}

        {(session || status) && (
          <details className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
            <summary className="cursor-pointer font-semibold text-slate-900">Jason-only diagnostics</summary>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-slate-500">Profile</dt>
                <dd>{selectedProfile?.displayName}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Provider</dt>
                <dd>{selectedVoice?.provider}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Model</dt>
                <dd>{selectedVoice?.modelId}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Connection</dt>
                <dd>{status?.officialStreamer?.browserReady ? 'Ready' : 'Preparing'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Approved plan</dt>
                <dd>{session?.applicationId ? `${session.questionCount} job questions` : 'Private one-question diagnostic'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">PCM publications</dt>
                <dd>{status?.officialStreamer?.pcmPublishCount ?? 0}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Duplicate PCM</dt>
                <dd>{status?.officialStreamer?.duplicatePcmPublishCount ?? 0}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Listener PCM bytes</dt>
                <dd>{status?.officialStreamer?.listenerPcmBytes ?? 0}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Recording</dt>
                <dd>{status?.recordingStarted ? 'Private candidate recording active' : 'Off'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Egress</dt>
                <dd>{status?.egressStarted ? 'Active' : 'Off'}</dd>
              </div>
            </dl>
          </details>
        )}
      </div>
    </EmployerPageShell>
  );
};

export default OwnedAvatarDemo;
